import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components';
import { getCourseById } from '../../services/course-service';
import { getQuizAttempts, QuizAttempt } from '../../services/quiz-attempt-service';
import { getGeneratedQuiz, generateQuiz, submitGeneratedQuiz, GeneratedQuestion } from '../../services/quiz-gen-service';

function msToCountdown(ms: number): string {
  if (ms <= 0) return '0m 0s';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s}s`;
}

type QuizState = 'checking' | 'not_generated' | 'generating' | 'ready' | 'submitted';

const Quiz = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseId = searchParams.get('courseId') || '';
  const moduleId = searchParams.get('moduleId') || '';

  const [quizState,       setQuizState]       = useState<QuizState>('checking');
  const [questions,       setQuestions]        = useState<GeneratedQuestion[]>([]);
  const [, setPassingScore]                    = useState(70);
  const [answers,         setAnswers]          = useState<string[]>([]);
  const [currentQuestion, setCurrentQuestion]  = useState(0);
  const [attempts,        setAttempts]         = useState<QuizAttempt[]>([]);
  const [attemptsLoading, setAttemptsLoading]  = useState(true);
  const [result,          setResult]           = useState<{ score: number; correct: number; total: number; passed: boolean; passingScore: number } | null>(null);
  const [submitting,      setSubmitting]       = useState(false);
  const [generateError,   setGenerateError]    = useState('');
  const [countdown,       setCountdown]        = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const alreadyPassed  = attempts.some(a => a.passed);
  const recentAttempts = attempts.filter(a => Date.now() - new Date(a.createdAt).getTime() < 3_600_000);
  const isLocked       = recentAttempts.length >= 3;
  const nextAvailable  = useMemo(
    () => isLocked ? new Date(new Date(recentAttempts[0].createdAt).getTime() + 3_600_000) : null,
    [isLocked, recentAttempts]
  );
  const attemptsUsed   = recentAttempts.length;
  const attemptsLeft   = Math.max(0, 3 - attemptsUsed);

  useEffect(() => {
    if (!courseId || !id) { setAttemptsLoading(false); return; }
    getQuizAttempts(courseId, id)
      .then(a => setAttempts(a))
      .catch(() => {})
      .finally(() => setAttemptsLoading(false));
  }, [courseId, id]);

  useEffect(() => {
    if (!courseId || !id) return;
    getCourseById(courseId).then(course => {
      for (const mod of course.modules || []) {
        for (const item of mod.items) {
          if (String(item._id) === id && item.type === 'quiz') {
            setPassingScore(item.passingScore || 70);
            break;
          }
        }
      }
    }).catch(() => {});
  }, [courseId, id]);

  useEffect(() => {
    if (!courseId || !moduleId || !id) { setQuizState('not_generated'); return; }
    getGeneratedQuiz(courseId, moduleId, id)
      .then(data => {
        if (data.generated && data.questions) {
          setQuestions(data.questions);
          setAnswers(new Array(data.questions.length).fill(''));
          setQuizState('ready');
        } else {
          setQuizState('not_generated');
        }
      })
      .catch(() => setQuizState('not_generated'));
  }, [courseId, moduleId, id]);

  useEffect(() => {
    if (!isLocked || !nextAvailable) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const tick = () => {
      const remaining = nextAvailable.getTime() - Date.now();
      setCountdown(remaining <= 0 ? '0m 0s' : msToCountdown(remaining));
      if (remaining <= 0 && timerRef.current) clearInterval(timerRef.current);
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isLocked, nextAvailable]);

  const backLink = courseId ? `/app/lms/courses/${courseId}` : '/app/lms/courses';

  const handleGenerate = async () => {
    if (!courseId || !moduleId || !id) return;
    setQuizState('generating');
    setGenerateError('');
    try {
      const data = await generateQuiz(courseId, moduleId, id);
      if (data.generated && data.questions) {
        setQuestions(data.questions);
        setAnswers(new Array(data.questions.length).fill(''));
        setQuizState('ready');
      } else {
        setGenerateError('Generation failed. Please try again.');
        setQuizState('not_generated');
      }
    } catch (e: any) {
      setGenerateError(e.message || 'Generation failed. Please try again.');
      setQuizState('not_generated');
    }
  };

  const setAnswer = (idx: number, value: string) =>
    setAnswers(prev => { const a = [...prev]; a[idx] = value; return a; });

  const handleFinish = async () => {
    if (!courseId || !moduleId || !id || submitting) return;
    setSubmitting(true);
    try {
      const res = await submitGeneratedQuiz(courseId, moduleId, id, answers);
      setResult({ score: res.score, correct: res.correct, total: res.total, passed: res.passed, passingScore: res.passingScore });
      setAttempts(prev => [...prev, res.attempt]);
      setQuizState('submitted');
    } catch (e: any) {
      alert(e.message || 'Submission failed. Try again.');
    }
    setSubmitting(false);
  };

  const resetQuiz = () => {
    setAnswers(new Array(questions.length).fill(''));
    setCurrentQuestion(0);
    setResult(null);
    setQuizState('ready');
  };

  const BackLink = () => (
    <Link to={backLink} className="text-muted hover:text-text text-sm inline-flex items-center gap-1 mb-3">
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      Back to Course
    </Link>
  );

  if (attemptsLoading || quizState === 'checking') {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (alreadyPassed && quizState !== 'submitted') {
    return (
      <div className="section-gap">
        <div className="border-b border-line pb-4"><BackLink /><h1 className="text-3xl font-bold text-text">Quiz</h1></div>
        <div className="text-center py-12">
          <div className="w-24 h-24 rounded-full border-4 border-success bg-success/10 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-success" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-success mb-2">Already Passed</h2>
          <p className="text-muted mb-8">You've already completed this quiz successfully.</p>
          <Button variant="primary" onClick={() => navigate(backLink)}>Back to Course</Button>
        </div>
      </div>
    );
  }

  if (isLocked && quizState !== 'submitted') {
    return (
      <div className="section-gap">
        <div className="border-b border-line pb-4"><BackLink /><h1 className="text-3xl font-bold text-text">Quiz</h1></div>
        <div className="text-center py-12">
          <div className="w-24 h-24 rounded-full border-4 border-warning bg-warning/10 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-warning mb-2">Quiz Locked</h2>
          <p className="text-muted mb-2">You've used all 3 attempts for this hour.</p>
          <p className="text-text font-semibold text-lg mb-8">
            Next attempt in <span className="text-chart">{countdown}</span>
          </p>
          <Button variant="ghost" onClick={() => navigate(backLink)}>Back to Course</Button>
        </div>
      </div>
    );
  }

  if (quizState === 'not_generated' || quizState === 'generating') {
    return (
      <div className="section-gap">
        <div className="border-b border-line pb-4"><BackLink /><h1 className="text-3xl font-bold text-text">Quiz</h1></div>
        <div className="text-center py-16">
          <div className="w-24 h-24 rounded-full border-4 border-chart bg-chart/10 flex items-center justify-center mx-auto mb-6">
            {quizState === 'generating' ? (
              <div className="w-10 h-10 border-2 border-chart border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-10 h-10 text-chart" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .03 2.798-1.32 2.798H4.118c-1.35 0-2.318-1.798-1.319-2.798L4 15.3" />
              </svg>
            )}
          </div>
          <h2 className="text-2xl font-bold text-text mb-3">
            {quizState === 'generating' ? 'Generating your quiz…' : 'Ready to test your knowledge?'}
          </h2>
          <p className="text-muted mb-6 max-w-sm mx-auto">
            {quizState === 'generating'
              ? 'AI is creating a personalised quiz based on course material. This may take a moment.'
              : "Click below to generate a personalised quiz based on what you've studied so far."}
          </p>
          {generateError && <p className="text-danger text-sm mb-4">{generateError}</p>}
          {quizState !== 'generating' && (
            <Button variant="primary" onClick={handleGenerate}>Generate Quiz</Button>
          )}
        </div>
      </div>
    );
  }

  if (quizState === 'submitted' && result) {
    const canRetry = !isLocked && !alreadyPassed;
    return (
      <div className="section-gap">
        <div className="border-b border-line pb-4">
          <BackLink />
          <h1 className="text-3xl font-bold text-text">Quiz Results</h1>
        </div>
        <div className="text-center py-8">
          <div className={`w-28 h-28 rounded-full border-4 flex items-center justify-center mx-auto mb-6 animate-fade-in ${result.passed ? 'border-success bg-success/10' : 'border-danger bg-danger/10'}`}>
            <div>
              <div className={`text-3xl font-bold ${result.passed ? 'text-success' : 'text-danger'}`}>{result.score}%</div>
              <div className="text-xs text-muted">{result.correct}/{result.total}</div>
            </div>
          </div>
          <h2 className={`text-2xl font-bold mb-2 ${result.passed ? 'text-success' : 'text-danger'}`}>
            {result.passed ? 'Passed!' : 'Not quite'}
          </h2>
          <p className="text-muted mb-2">
            {result.passed
              ? 'Great work! You passed this quiz.'
              : `You need ${result.passingScore}% to pass.${canRetry ? ' Try again!' : ''}`}
          </p>
          {!result.passed && isLocked && (
            <p className="text-warning text-sm mb-4">
              Quiz locked — next attempt in <span className="font-semibold">{countdown}</span>
            </p>
          )}
          <div className="flex gap-3 justify-center mt-8">
            {!result.passed && canRetry && (
              <Button variant="primary" onClick={resetQuiz}>Try Again</Button>
            )}
            <Button
              variant={result.passed ? 'primary' : 'ghost'}
              onClick={() => navigate(result.passed ? `${backLink}?quizPassed=1` : backLink)}
            >
              {result.passed ? 'Continue' : 'Back to Course'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz UI
  const currentQ       = questions[currentQuestion];
  const pct            = ((currentQuestion + 1) / questions.length) * 100;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const isAnswered     = (i: number) => answers[i] !== '';
  const allAnswered    = questions.every((_, i) => isAnswered(i));

  return (
    <div className="section-gap">
      <div className="border-b border-line pb-4">
        <BackLink />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text">Quiz</h1>
            <p className="text-muted mt-1">Question {currentQuestion + 1} of {questions.length}</p>
          </div>
          <div className="text-right">
            <span className="text-sm text-muted">Attempt {attemptsUsed + 1} of 3</span>
            <p className={`text-xs font-medium mt-0.5 ${attemptsLeft <= 1 ? 'text-danger' : 'text-muted'}`}>
              {attemptsLeft} {attemptsLeft === 1 ? 'attempt' : 'attempts'} remaining
            </p>
          </div>
        </div>
        <div className="w-full bg-surface rounded-full h-1.5 mt-4">
          <div className="bg-chart h-1.5 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="two-col">
        <div className="border border-line rounded-xl p-6">
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-text">{currentQ.question}</h2>
            <div className="space-y-3">
              {currentQ.options.map((option, index) => {
                const val = currentQ.type === 'true_false'
                  ? (index === 0 ? 'true' : 'false')
                  : String(index);
                return (
                  <button
                    key={index}
                    onClick={() => setAnswer(currentQuestion, val)}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      answers[currentQuestion] === val
                        ? 'border-chart bg-chart/10 text-text'
                        : 'border-line bg-surface/30 hover:bg-surface/50 text-text'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${answers[currentQuestion] === val ? 'border-chart bg-chart' : 'border-muted'}`} />
                      <span>{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border border-line rounded-xl p-5">
          <h3 className="text-text font-semibold mb-4">Quiz Progress</h3>
          <div className="space-y-3 mb-6">
            {questions.map((_, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                  index === currentQuestion ? 'bg-chart' : isAnswered(index) ? 'bg-success' : 'bg-muted/30 border border-line'
                }`} />
                <span className="text-sm text-muted">Question {index + 1}{isAnswered(index) ? ' ✓' : ''}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => setCurrentQuestion(q => q - 1)} disabled={currentQuestion === 0}>Previous</Button>
            {isLastQuestion ? (
              <Button variant="primary" size="sm" onClick={handleFinish} disabled={!allAnswered || submitting}>
                {submitting ? 'Submitting…' : 'Finish Quiz'}
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={() => setCurrentQuestion(q => q + 1)} disabled={!isAnswered(currentQuestion)}>Next</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
